import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Volume2, AudioLines, Vibrate, VolumeX } from 'lucide-react';
import {
  playScanFeedback, unlockAudio, setScanFeedback,
  availableFeedbackOptions, resolveScanFeedback,
} from '@/lib/scanFeedback';

const FB_META = {
  beep: { icon: Volume2, label: 'Beep' },
  voice: { icon: AudioLines, label: 'Voice' },
  vibrate: { icon: Vibrate, label: 'Buzz' },
  off: { icon: VolumeX, label: 'Off' },
};

/**
 * Compact inline barcode scanner.
 *
 * Renders a small (~120px) live camera preview that sits directly inside a form
 * (no modal / bottom sheet). The camera stays on for continuous scanning, emits
 * one `onDetected(code)` per physical barcode (frame/repeat debounced), gives
 * device-appropriate feedback (audio/voice/vibration where supported), and fully
 * releases the camera on close / unmount / navigation away.
 *
 * iOS note: removing the old startup delay keeps camera init closer to the
 * opening tap, but this does NOT guarantee iOS Safari/PWA camera compatibility —
 * permission handling and codec support still depend on the device/OS.
 */
export default function InlineScanner({ onDetected, onClose, dedupeMs = 2000 }) {
  const regionId = useRef(`inline-scan-${Math.random().toString(36).slice(2)}`).current;
  const qrRef = useRef(null);
  const startedRef = useRef(false); // guards against double-start on rapid taps
  const [camError, setCamError] = useState('');
  const [starting, setStarting] = useState(true);
  const [pulse, setPulse] = useState(false);

  // Device-aware feedback options + a safe effective mode.
  const options = useRef(availableFeedbackOptions()).current;
  const [fb, setFb] = useState('beep');
  const [fbNote, setFbNote] = useState('');

  // One physical scan = one onDetected (debounce the many frames per barcode).
  const lastScan = useRef({ code: '', ts: 0 });

  // Resolve the saved preference against this device's capabilities once.
  useEffect(() => {
    const { mode, changed, from } = resolveScanFeedback();
    setFb(mode);
    if (changed) {
      const label = (FB_META[from] && FB_META[from].label) || from;
      setFbNote(`${label} isn't supported on this device — switched to ${FB_META[mode].label}.`);
    }
  }, []);

  // Camera lifecycle: start once on mount, always release on unmount.
  useEffect(() => {
    let cancelled = false;
    if (startedRef.current) return undefined; // never start twice
    startedRef.current = true;

    const qr = new Html5Qrcode(regionId, { verbose: false });
    qrRef.current = qr;

    (async () => {
      try {
        await qr.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            // Clamp the scan box to the (small) preview so html5-qrcode never
            // throws when the video is shorter than a fixed box.
            qrbox: (vw, vh) => ({ width: Math.min(240, Math.max(120, vw - 24)), height: Math.min(96, Math.max(60, vh - 24)) }),
          },
          (decodedText) => handleDetected(decodedText),
          () => { /* per-frame decode failure: ignore (normal) */ }
        );
        if (cancelled) {
          // Scanner was closed while the camera was still starting up — stop now.
          try { await qr.stop(); } catch (e) { /* ignore */ }
          try { qr.clear(); } catch (e) { /* ignore */ }
          return;
        }
        setStarting(false);
      } catch (e) {
        if (cancelled) return;
        const name = String((e && (e.name || e.message)) || '');
        let msg = 'Camera unavailable. Type the code manually.';
        if (/NotAllowed|Permission|denied/i.test(name)) msg = 'Camera permission blocked. Allow camera access in your browser settings, or type the code manually.';
        else if (/NotFound|Devices|OverconstrainedError/i.test(name)) msg = 'No camera found on this device. Type the code manually.';
        else if (/NotReadable|TrackStart|AbortError/i.test(name)) msg = 'Camera is busy or unavailable. Close other camera apps, or type the code manually.';
        setCamError(msg);
        setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      const inst = qrRef.current;
      qrRef.current = null;
      if (inst) {
        // Stop the stream and release the camera hardware.
        try {
          inst.stop().then(() => { try { inst.clear(); } catch (e) { /* ignore */ } }).catch(() => {});
        } catch (e) { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDetected = (code) => {
    const clean = String(code || '').trim().toUpperCase();
    if (!clean) return;
    const now = Date.now();
    const last = lastScan.current;
    // Ignore repeat detections of the SAME stationary code within the window.
    if (last.code === clean && now - last.ts < dedupeMs) return;
    // Short global lock so two rapid frames can't both fire before processing.
    if (now - last.ts < 300) return;
    lastScan.current = { code: clean, ts: now };
    playScanFeedback();
    setPulse(true);
    setTimeout(() => setPulse(false), 220);
    onDetected(clean);
  };

  const chooseFb = (opt) => {
    // This runs inside a real tap -> good moment to unlock audio on iOS.
    unlockAudio();
    setScanFeedback(opt);
    setFb(opt);
    setFbNote('');
    // Immediate preview of the chosen feedback (except 'off').
    if (opt !== 'off') { try { playScanFeedback(); } catch (e) { /* ignore */ } }
  };

  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-border bg-black" data-testid="inline-scanner">
      <div className="relative" style={{ height: 120 }}>
        <div
          id={regionId}
          className="h-full w-full [&_video]:!h-[120px] [&_video]:!w-full [&_video]:!object-cover"
          style={{ height: 120 }}
        />
        {/* Horizontal scanning frame (brackets + sweep line) */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className={`relative h-[70px] w-[74%] transition-transform duration-150 ${pulse ? 'scale-105' : ''}`}>
            <span className="absolute -left-px -top-px h-5 w-5 rounded-tl-md border-l-2 border-t-2 border-primary" />
            <span className="absolute -right-px -top-px h-5 w-5 rounded-tr-md border-r-2 border-t-2 border-primary" />
            <span className="absolute -bottom-px -left-px h-5 w-5 rounded-bl-md border-b-2 border-l-2 border-primary" />
            <span className="absolute -bottom-px -right-px h-5 w-5 rounded-br-md border-b-2 border-r-2 border-primary" />
            <span className={`absolute left-2 right-2 top-1/2 h-0.5 bg-primary ${pulse ? 'opacity-100' : 'opacity-70 animate-pulse'}`} />
          </div>
        </div>
        {/* Success flash */}
        <div className={`pointer-events-none absolute inset-0 bg-emerald-400/30 transition-opacity duration-150 ${pulse ? 'opacity-100' : 'opacity-0'}`} />
        <button
          type="button"
          onClick={onClose}
          data-testid="inline-scanner-close"
          aria-label="Close scanner"
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-4 w-4" />
        </button>
        {starting && !camError && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-white/80">Starting camera…</div>
        )}
      </div>
      {camError
        ? <p className="px-3 py-2 text-xs font-medium text-amber-300" data-testid="inline-scanner-camerror">{camError}</p>
        : <p className="px-3 pt-1.5 text-center text-[11px] text-white/70">Point at a barcode — camera stays on for continuous scanning</p>}
      {fbNote && (
        <p className="px-3 pb-0.5 text-center text-[11px] text-amber-300" data-testid="scan-feedback-note">{fbNote}</p>
      )}
      {/* Feedback switch: let each shop pick beep / voice / buzz / off (only modes this device supports) */}
      <div className="flex items-center justify-center gap-1 px-2 py-1.5" data-testid="scan-feedback-switch" role="group" aria-label="Scan feedback">
        {options.map((opt) => {
          const { icon: Icon, label } = FB_META[opt];
          const active = fb === opt;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => chooseFb(opt)}
              data-testid={`scan-fb-${opt}`}
              aria-pressed={active}
              className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${active ? 'bg-primary text-primary-foreground' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}
            >
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
